<script setup lang="ts">
import { getCurrentCountryIso31662 } from '@@/bits/current_locale'
import { formatPhoneNumber } from '@@/bits/phone_number'
import { getAnchorFromElement } from '@@/library/v4/components/OzPopoverModal.vue'
import { useSurfacePanelsStore } from '@@/pinia/surface_panels'
import type { PopoverAnchor } from '@@/types'
import type { PostPropertyDisplayAttributes } from '@@/vuecomponents/SurfacePostProperties.vue'
import { ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    darkMode: boolean
    postProperty: PostPropertyDisplayAttributes
    propertyValueClass?: string
    as?: string
  }>(),
  {
    propertyValueClass: 'text-body-small',
    as: 'dd',
  },
)

const surfacePanelsStore = useSurfacePanelsStore()
const countryCode = getCurrentCountryIso31662()

const handlePhoneNumberClick = (e: PointerEvent) => {
  const popoverAnchor = {
    position: 'top',
    ...getAnchorFromElement(e.target as HTMLElement),
  } as PopoverAnchor
  surfacePanelsStore.openPhoneNumberPopover({
    value: props.postProperty.formattedValue,
    anchor: popoverAnchor,
  })
}

const displayedPhoneNumber = ref(props.postProperty.formattedValue)

watch(
  () => props.postProperty.formattedValue,
  async () => {
    if (props.postProperty.formattedValue) {
      displayedPhoneNumber.value = await formatPhoneNumber(props.postProperty.formattedValue.toString(), countryCode)
    }
  },
  {
    immediate: true,
  },
)
</script>

<template>
  <component
    :is="as"
    :class="[
      'phone-number-field-value',
      propertyValueClass,
      darkMode && 'text-light-text-100',
      !darkMode && 'text-dark-text-100',
    ]"
  >
    <a
      :href="`tel:${postProperty.formattedValue}`"
      data-testid="surfacePostPropertiesPhoneNumberFieldValue"
      @click.prevent="handlePhoneNumberClick"
      @keydown.space.prevent="handlePhoneNumberClick"
      >{{ displayedPhoneNumber }}</a
    >
  </component>
</template>
<style scoped>
.phone-number-field-value a {
  @apply underline;
  @apply text-current;
  text-underline-position: from-font;
  text-decoration-thickness: from-font;
}
</style>
